import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { CheckCircle2, AlertTriangle, AlertCircle, ChevronDown, ChevronRight, Undo2, ExternalLink, Send, ShieldAlert, WifiOff, ArrowLeft } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { motion } from 'framer-motion';
import ErrorTable from '../../bulk-stock-upload/ErrorTable';
import { useBulkStocktakeUpload } from '../useBulkStocktakeUpload';

interface ResultStepProps {
  onUploadAnother: () => void;
  onUndo: () => void;
  isUndoing: boolean;
  onClose: () => void;
  onGoBack: () => void;
}

/**
 * Shared by the plain-success screen and the mixed own-plus-client screen:
 * both can have applied rows to reverse, and the confirm must read the same
 * on either.
 */
const UndoConfirmDialog: React.FC<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  successCount: number;
  isUndoing: boolean;
  onUndo: () => void;
}> = ({ open, onOpenChange, successCount, isUndoing, onUndo }) => (
  <AlertDialog open={open} onOpenChange={(next) => !next && onOpenChange(false)}>
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle className="flex items-center gap-2">
          <AlertCircle className="h-5 w-5 text-amber-500" />
          Undo Entire Stocktake?
        </AlertDialogTitle>
        <AlertDialogDescription asChild>
          <div className="space-y-3">
            <p>
              Each SKU will be reset to its stock level from before the
              stocktake was applied.
            </p>
            <div className="rounded-md border bg-muted/50 p-3 text-sm">
              <p className="font-medium text-foreground">
                Impact: {successCount} SKU{successCount !== 1 ? 's' : ''} will be reverted
              </p>
            </div>
            <p className="text-amber-600 dark:text-amber-400 text-xs">
              If stock has been added or removed since then, those changes will be overwritten.
            </p>
          </div>
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel disabled={isUndoing} onClick={() => onOpenChange(false)}>
          Cancel
        </AlertDialogCancel>
        <AlertDialogAction
          onClick={() => {
            onOpenChange(false);
            onUndo();
          }}
          disabled={isUndoing}
          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
        >
          {isUndoing ? 'Undoing...' : 'Confirm Undo'}
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

const ResultStep: React.FC<ResultStepProps> = ({
  onUploadAnother,
  onUndo,
  isUndoing,
  onClose,
  onGoBack,
}) => {
  const { t } = useTranslation('inventory');
  const { result } = useBulkStocktakeUpload();
  const navigate = useNavigate();
  const [errorsOpen, setErrorsOpen] = useState(false);
  const [showUndoConfirm, setShowUndoConfirm] = useState(false);

  if (!result) return null;

  const { successCount, failedCount, validationErrors, executionErrors } = result;
  const totalErrors = validationErrors.length + executionErrors.length;
  const isFullSuccess = failedCount === 0 && totalErrors === 0;

  // Merge execution errors into the validation error format for display
  const allErrors = [
    ...validationErrors,
    ...executionErrors.map((msg) => ({
      row: 0,
      sku: '--',
      reason: msg,
    })),
  ];

  // --- Blocked: all validation errors, no stock changed ---
  if (result.mode === 'blocked') {
    return (
      <div className="space-y-4">
        <div className="flex flex-col items-center gap-3 py-4">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', stiffness: 260, damping: 20 }}
          >
            <ShieldAlert className="h-12 w-12 text-red-500" />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="text-center"
          >
            <h3 className="text-lg font-semibold">Upload Blocked</h3>
            <p className="text-sm text-muted-foreground mt-1">
              {failedCount} validation {failedCount === 1 ? 'error' : 'errors'} found. No stock was changed.
            </p>
          </motion.div>
        </div>
        <div className="flex items-center justify-center">
          <Badge variant="destructive" size="lg">
            {failedCount} {failedCount === 1 ? 'error' : 'errors'} to fix
          </Badge>
        </div>
        {allErrors.length > 0 && <ErrorTable errors={allErrors} />}
        <div className="flex justify-end gap-2 pt-2">
          <Button
            onClick={onGoBack}
            className="bg-emerald-600 text-white hover:bg-emerald-700 gap-1.5"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Go Back &amp; Fix
          </Button>
        </div>
      </div>
    );
  }

  // --- Network error: request may or may not have succeeded ---
  if (result.mode === 'network_error') {
    return (
      <div className="space-y-4">
        <div className="flex flex-col items-center gap-3 py-4">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', stiffness: 260, damping: 20 }}
          >
            <WifiOff className="h-12 w-12 text-amber-500" />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="text-center max-w-sm"
          >
            <h3 className="text-lg font-semibold">Connection Lost</h3>
            <p className="text-sm text-muted-foreground mt-1">
              The request timed out before a response was received. Your upload may have still succeeded.
            </p>
          </motion.div>
        </div>
        <Alert className="border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30">
          <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
          <AlertDescription className="text-amber-800 dark:text-amber-300 text-sm">
            Check the <strong>Logs</strong> page to confirm whether the upload was processed.
          </AlertDescription>
        </Alert>
        <div className="flex items-center justify-between gap-2 pt-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              navigate('/logs?tab=bulk_imports');
              onClose();
            }}
            className="gap-1.5"
          >
            <ExternalLink className="h-3.5 w-3.5" />
            Check Logs
          </Button>
          <Button
            onClick={onUploadAnother}
            className="bg-emerald-600 text-white hover:bg-emerald-700"
          >
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  // --- BLU-711 A2: staged for the client's approval ---
  // No tick, no "complete" on the staged part: those figures have not moved.
  // A mixed sheet (own rows + a client's rows at a shared location) comes back
  // with a batchId AND proposals, so BOTH halves get stated: claiming "nothing
  // has been applied" while a batch sits in the ledger is a lie the operator
  // acts on.
  const proposals = result.proposals ?? [];
  if (proposals.length > 0 && isFullSuccess) {
    const stagedLines = proposals.reduce((sum, p) => sum + p.lineCount, 0);
    const appliedAnything = !!result.batchId && successCount > 0;
    return (
      <div className="space-y-4">
        <div className="flex flex-col items-center gap-3 py-4">
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.22, ease: [0.4, 0, 0.2, 1] }}
            className="text-center"
          >
            <Send className="mx-auto h-10 w-10 text-threadheads-purple" aria-hidden="true" />
            <h3 className="mt-3 text-lg font-semibold">
              {appliedAnything
                ? t('bulkStocktake.result.mixedTitle')
                : t('bulkStocktake.result.sentTitle')}
            </h3>
            <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground">
              {appliedAnything
                ? t('bulkStocktake.result.mixedBody', {
                    count: stagedLines,
                    applied: successCount,
                    staged: stagedLines,
                  })
                : t('bulkStocktake.result.sentBody', { count: stagedLines })}
            </p>
          </motion.div>
        </div>

        {appliedAnything && (
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Badge variant="success" size="lg">
              {t('bulkStocktake.result.appliedBadge', { count: successCount })}
            </Badge>
            <Badge variant="secondary" size="lg">
              {t('bulkStocktake.result.stagedBadge', { count: stagedLines })}
            </Badge>
          </div>
        )}

        <div className="flex items-center justify-between gap-2 pt-2">
          {appliedAnything ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowUndoConfirm(true)}
              disabled={isUndoing}
              className="gap-1.5 text-destructive hover:text-destructive"
            >
              <Undo2 className="h-3.5 w-3.5" />
              {isUndoing
                ? t('bulkStocktake.result.undoing')
                : t('bulkStocktake.result.undoApplied')}
            </Button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onClose}>
              {t('bulkStocktake.result.done')}
            </Button>
            <Button variant="outline" size="sm" onClick={onUploadAnother}>
              {t('bulkStocktake.result.uploadAnother')}
            </Button>
            {/* The approval surface is where the rest of this count now lives,
                so it is the primary, in the one accent this app uses. */}
            <Button
              size="sm"
              onClick={() => {
                navigate('/inventory/stocktakes');
                onClose();
              }}
              className="gap-1.5 bg-threadheads-purple text-white hover:bg-threadheads-purple/90"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              {t('bulkStocktake.result.viewInStocktakes')}
            </Button>
          </div>
        </div>

        <UndoConfirmDialog
          open={showUndoConfirm}
          onOpenChange={setShowUndoConfirm}
          successCount={successCount}
          isUndoing={isUndoing}
          onUndo={onUndo}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Success / partial failure header */}
      <div className="flex flex-col items-center gap-3 py-4">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', stiffness: 260, damping: 20 }}
        >
          {isFullSuccess ? (
            <CheckCircle2 className="h-12 w-12 text-green-500" />
          ) : (
            <AlertTriangle className="h-12 w-12 text-amber-500" />
          )}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="text-center"
        >
          <h3 className="text-lg font-semibold">
            {isFullSuccess ? 'Stocktake Complete' : 'Stocktake Completed with Errors'}
          </h3>
        </motion.div>
      </div>

      {/* Stats */}
      <div className="flex items-center justify-center gap-3">
        <Badge variant="success" size="lg">
          {successCount} successful
        </Badge>
        {failedCount > 0 && (
          <Badge variant="destructive" size="lg">
            {failedCount} failed
          </Badge>
        )}
      </div>

      {/* Errors section */}
      {allErrors.length > 0 && (
        <Collapsible open={errorsOpen} onOpenChange={setErrorsOpen}>
          <CollapsibleTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-between px-3"
            >
              <span className="text-sm">
                {errorsOpen ? 'Hide' : 'Show'} {allErrors.length} error
                {allErrors.length !== 1 ? 's' : ''}
              </span>
              {errorsOpen ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent className="pt-2">
            <ErrorTable errors={allErrors} />
          </CollapsibleContent>
        </Collapsible>
      )}

      {/* Actions */}
      <div className="flex flex-col gap-2 pt-2">
        <div className="flex items-center justify-between gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              navigate('/logs?tab=bulk_imports');
              onClose();
            }}
            className="gap-1.5"
          >
            <ExternalLink className="h-3.5 w-3.5" />
            View in Logs
          </Button>

          <div className="flex gap-2">
            {isFullSuccess && result.batchId && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowUndoConfirm(true)}
                disabled={isUndoing}
                className="gap-1.5 text-destructive hover:text-destructive"
              >
                <Undo2 className="h-3.5 w-3.5" />
                {isUndoing ? 'Undoing...' : 'Undo Entire Stocktake'}
              </Button>
            )}

            <Button variant="outline" size="sm" onClick={onClose}>
              Done
            </Button>

            <Button
              size="sm"
              onClick={onUploadAnother}
              className="bg-emerald-600 text-white hover:bg-emerald-700"
            >
              Upload Another
            </Button>
          </div>
        </div>
      </div>

      {/* Undo confirmation dialog */}
      <UndoConfirmDialog
        open={showUndoConfirm}
        onOpenChange={setShowUndoConfirm}
        successCount={successCount}
        isUndoing={isUndoing}
        onUndo={onUndo}
      />
    </div>
  );
};

export default ResultStep;
